import { FC, useEffect } from 'react';
import {
  useActiveAccount,
  useActiveWallet,
  useActiveWalletChain,
} from 'thirdweb/react';

import { useConnector, useEthers } from '@usedapp/core';
import { EIP1193 } from 'thirdweb/wallets';

import { thirdwebBridgeConnector } from '@context/connectors/ThirdwebBridgeConnector';
import { useTrackedState } from '@context/store';
import { client, getThirdwebChain } from '@context/thirdweb';

/**
 * Bridges thirdweb's <ConnectButton> wallet state into @usedapp/core so every
 * existing useEthers()/useConnector() consumer elsewhere in the app (Swap,
 * Pools, Farms, Staking, Dashboard, ...) keeps working unchanged. Mirrors the
 * "sync bridge" pattern used in simple-payment-link-verse/poc-dca to connect
 * thirdweb into wagmi, targeting @usedapp/core's Connector API instead.
 * Renders nothing — mount once alongside the connect button.
 */
const ThirdwebSync: FC = () => {
  const { isWallet } = useTrackedState();
  const { activateBrowserWallet, deactivate } = useEthers();
  const { connector } = useConnector();
  const thirdwebAccount = useActiveAccount();
  const thirdwebWallet = useActiveWallet();
  const thirdwebChain = useActiveWalletChain();

  const isBridgeActive = connector?.connector === thirdwebBridgeConnector;

  useEffect(() => {
    if (
      !client ||
      !thirdwebAccount?.address ||
      !thirdwebWallet ||
      !thirdwebChain?.id
    ) {
      return;
    }

    const chain = getThirdwebChain(thirdwebChain.id);
    const raw = EIP1193.toProvider({ wallet: thirdwebWallet, chain, client });

    if (isBridgeActive) {
      thirdwebBridgeConnector.sync(raw, thirdwebChain.id, [
        thirdwebAccount.address,
      ]);
      return;
    }

    thirdwebBridgeConnector.stage(raw, thirdwebChain.id, [
      thirdwebAccount.address,
    ]);

    // activateBrowserWallet is typed as returning void but is actually
    // async under the hood (and can reject — e.g. a transient RPC
    // failure while fetching the block number during activation).
    // Without catching this, usedapp's account/library silently never
    // get set while thirdweb's own connection state stays fine, leaving
    // every useEthers() consumer (Swap, Pools, Farms, Staking, ...)
    // looking disconnected despite the header showing a connected
    // wallet.
    Promise.resolve(
      activateBrowserWallet({
        type: 'thirdwebBridge',
      }) as unknown as Promise<void>,
    ).catch(error => {
      // eslint-disable-next-line no-console
      console.error(
        'ThirdwebSync: failed to bridge wallet into usedapp',
        error,
      );
    });
  }, [
    thirdwebAccount?.address,
    thirdwebWallet?.id,
    thirdwebChain?.id,
    isBridgeActive,
    activateBrowserWallet,
  ]);

  // Propagate disconnects the other way: if the thirdweb session is gone
  // (e.g. user hit Disconnect in the details modal) but the bridge connector
  // is still the active usedapp connector, tear down the usedapp session too.
  useEffect(() => {
    if (thirdwebAccount || !isBridgeActive) return;
    deactivate();
  }, [thirdwebAccount, isBridgeActive, deactivate]);

  // Clear stale @usedapp/core auto-reconnects (MetaMask, WalletConnect, …)
  // when thirdweb owns the connect UI but has no active session. Without
  // this, the header shows "Connect" while wallet-specific pages still read
  // a ghost usedapp account from a previous session.
  useEffect(() => {
    if (!client || isWallet || thirdwebAccount?.address) return;

    const connectorName = connector?.connector?.name?.toLowerCase() ?? '';
    const isStaleSession =
      isBridgeActive ||
      connectorName === 'metamask' ||
      connectorName === 'walletconnect' ||
      connectorName === 'rabby' ||
      connectorName === 'wcuniversal';

    if (isStaleSession) {
      deactivate();
    }
  }, [
    client,
    isWallet,
    thirdwebAccount?.address,
    isBridgeActive,
    connector,
    deactivate,
  ]);

  return null;
};

export default ThirdwebSync;
